// regionMoveSend — 範囲の移動・コピーを server に送る use case
// (docs/_wip/region-move-2026-10-15.md)。UI は [確定] で `sendRegionMove` を呼ぶだけ。
//
// 流れ:
//   1. engine.holdOwnStrokeInput (自分の新しい線を止める) → 描いている線が終わるのを待つ
//   2. 各 item の srcLayerId の src 矩形を engine.readLayerRegion → encodeRgbaPng (純 JS)
//   3. whiteboard.region-request → whiteboard.region-token を待つ (rejected ならここで終わり)
//   4. 各 item の PUT (並列、`runImagepastePut`) → whiteboard.region-confirm
//   5. 自分の whiteboard.region の echo (= 適用済み) か、region-rejected を待つ
//
// 保持 (hold) は成功なら engine が echo 適用で自動解除する (`releaseOnRegionFromUid`)。
// 失敗したら (rejected / PUT 失敗 / 時間切れ / 読み取り失敗) ここで外す。
// 適用は echo だけで行う (ローカルで先に適用しない = server が却下しうるため)。

import { encodeRgbaPng } from '@oekaki/drawing-core';
import type {
  ServerEvent,
  WhiteboardRegionRejected,
  WhiteboardRegionToken,
} from '@oekaki/protocol';
import { type ImagepasteFetchFn, runImagepastePut } from './imagepasteUpload.js';
import type { Transport } from './ports/Transport.js';

/** server が返す拒否 reason (protocol 由来)。 */
export type RegionRejectReason = WhiteboardRegionRejected['reason'];

/** 送る側で起きる、server 由来でない失敗。 */
export type RegionLocalFailure =
  | 'read-failed' // 層が無い / 範囲外 / 読み取れない / 読んだ大きさが src と違う
  | 'put-failed' // PUT が失敗した
  | 'token-mismatch' // token が items と対応しない
  | 'timeout'; // token または echo が時間内に戻らない

export type SendRegionMoveResult =
  | { readonly ok: true; readonly requestId: string }
  | {
      readonly ok: false;
      readonly requestId: string;
      readonly reason: RegionRejectReason | RegionLocalFailure;
      /** `rate-limited` のときのクールダウン残り ms。 */
      readonly retryAfterMs?: number;
      /** server / PUT が返したメッセージ。 */
      readonly message?: string;
    };

/** use case が engine に求める最小の面 (`CanvasDrawingEngine` が満たす)。 */
export interface RegionMoveEngine {
  readLayerRegion(
    layerId: number,
    x: number,
    y: number,
    w: number,
    h: number,
  ): Promise<{ width: number; height: number; rgba: Uint8ClampedArray } | null>;
  holdOwnStrokeInput(opts?: { releaseOnRegionFromUid?: string }): void;
  releaseOwnStrokeInput(): void;
  waitForOwnStrokesToEnd(): Promise<void>;
  /** 送る絵を渡す。自分の echo をこれで即適用する (R2 の fetch を待つ空白を作らない)。 */
  setOwnRegionImages?(
    images: ReadonlyArray<{
      srcLayerId: number;
      width: number;
      height: number;
      rgba: Uint8ClampedArray;
    }>,
  ): void;
}

export interface SendRegionMoveArgs {
  readonly transport: Transport;
  readonly engine: RegionMoveEngine;
  /** 自分の uid (echo の `whiteboard.region.uid` と突き合わせる)。 */
  readonly uid: string;
  readonly mode: 'move' | 'copy';
  /** 取った範囲 (キャンバス内の整数矩形)。 */
  readonly src: { readonly x: number; readonly y: number; readonly w: number; readonly h: number };
  /** 置き先の左上 (はみ出してよい)。 */
  readonly dst: { readonly x: number; readonly y: number };
  /**
   * 1〜`MAX_REGION_ITEMS`。複数のときは各 item で srcLayerId === dstLayerId
   * (server が検証)。1 つのときは別レイヤーへ置いてよい。
   */
  readonly items: ReadonlyArray<{ readonly srcLayerId: number; readonly dstLayerId: number }>;
  /**
   * [移動]/[コピー] を押した瞬間に写し取った絵 (items と同じ順)。渡すと確定時に読み直さず
   * これを送る = 浮かせていた絵と確定後の絵が一致する (設計メモ「使う絵は押した瞬間のもの」)。
   * 大きさが src と違うものがあれば `read-failed`。省略時は従来どおり確定時に engine から読む。
   */
  readonly captured?: ReadonlyArray<{
    readonly width: number;
    readonly height: number;
    readonly rgba: Uint8ClampedArray;
  }>;
  readonly putFetchFn?: ImagepasteFetchFn;
  readonly requestIdFn?: () => string;
  /** token を待つ上限 ms。既定 15 秒。 */
  readonly tokenTimeoutMs?: number;
  /** 確定から echo を待つ上限 ms。既定 20 秒 (engine の保持の上限と同じ)。 */
  readonly echoTimeoutMs?: number;
}

export const REGION_TOKEN_TIMEOUT_MS = 15_000;
export const REGION_ECHO_TIMEOUT_MS = 20_000;

type Waited<T> =
  | { readonly t: 'value'; readonly value: T }
  | { readonly t: 'end'; readonly r: SendRegionMoveResult };

export const sendRegionMove = async (args: SendRegionMoveArgs): Promise<SendRegionMoveResult> => {
  const { transport, engine, uid, mode, src, dst, items } = args;
  const requestId = (args.requestIdFn ?? (() => globalThis.crypto.randomUUID()))();
  const fail = (
    reason: RegionRejectReason | RegionLocalFailure,
    extra?: { retryAfterMs?: number; message?: string },
  ): SendRegionMoveResult => ({
    ok: false,
    requestId,
    reason,
    ...(extra?.retryAfterMs !== undefined ? { retryAfterMs: extra.retryAfterMs } : {}),
    ...(extra?.message !== undefined ? { message: extra.message } : {}),
  });

  // (1) 確定の前: 自分の新しい線を止め、描いている線が終わるのを待つ。
  engine.holdOwnStrokeInput({ releaseOnRegionFromUid: uid });
  let settled = false;
  const finishFail = (r: SendRegionMoveResult): SendRegionMoveResult => {
    settled = true;
    engine.releaseOwnStrokeInput();
    return r;
  };

  // server からの応答を購読 (request の前から。token / rejected / 自分の echo)。
  let onToken: ((t: WhiteboardRegionToken) => void) | null = null;
  let onEnd: ((r: SendRegionMoveResult) => void) | null = null;
  let tokenSeen: WhiteboardRegionToken | null = null;
  let echoSeen = false;
  const unsub = transport.onEvent((ev: ServerEvent) => {
    if (ev.t === 'whiteboard.region-token' && ev.requestId === requestId) {
      tokenSeen = ev;
      onToken?.(ev);
    } else if (ev.t === 'whiteboard.region-rejected' && ev.requestId === requestId) {
      onEnd?.(
        fail(ev.reason, {
          ...(ev.retryAfterMs !== undefined ? { retryAfterMs: ev.retryAfterMs } : {}),
          ...(ev.message !== undefined ? { message: ev.message } : {}),
        }),
      );
    } else if (ev.t === 'whiteboard.region' && ev.uid === uid) {
      echoSeen = true;
      onEnd?.({ ok: true, requestId });
    }
  });

  const timeoutResult = <T>(ms: number, p: Promise<T>): Promise<T | 'timeout'> =>
    new Promise((resolve) => {
      const timer = setTimeout(() => resolve('timeout'), ms);
      void p.then((v) => {
        clearTimeout(timer);
        resolve(v);
      });
    });

  try {
    await engine.waitForOwnStrokesToEnd();

    // (2) 各 item を読んで PNG にする。
    const pngs: Uint8Array[] = [];
    const reads: Array<{
      srcLayerId: number;
      width: number;
      height: number;
      rgba: Uint8ClampedArray;
    }> = [];
    for (const [i, item] of items.entries()) {
      const read =
        args.captured !== undefined
          ? (args.captured[i] ?? null)
          : await engine.readLayerRegion(item.srcLayerId, src.x, src.y, src.w, src.h);
      if (read === null || read.width !== src.w || read.height !== src.h) {
        return finishFail(fail('read-failed'));
      }
      reads.push({
        srcLayerId: item.srcLayerId,
        width: read.width,
        height: read.height,
        rgba: read.rgba,
      });
      try {
        pngs.push(encodeRgbaPng(read.rgba, read.width, read.height));
      } catch {
        return finishFail(fail('read-failed'));
      }
    }

    // echo は手元の写しで即適用する (fetch 待ちの空白を作らない)。
    engine.setOwnRegionImages?.(reads);

    // (3) request → token。
    const endPromise = new Promise<SendRegionMoveResult>((resolve) => {
      onEnd = resolve;
    });
    const tokenPromise = new Promise<WhiteboardRegionToken>((resolve) => {
      onToken = resolve;
      if (tokenSeen !== null) resolve(tokenSeen);
    });
    transport.send({
      t: 'whiteboard.region-request',
      requestId,
      mode,
      src: { x: src.x, y: src.y, w: src.w, h: src.h },
      dst: { x: dst.x, y: dst.y },
      items: items.map((item, i) => ({
        srcLayerId: item.srcLayerId,
        dstLayerId: item.dstLayerId,
        contentLength: (pngs[i] as Uint8Array).byteLength,
      })),
    });
    const tokenOrEnd = await timeoutResult(
      args.tokenTimeoutMs ?? REGION_TOKEN_TIMEOUT_MS,
      Promise.race([
        tokenPromise.then((value): Waited<WhiteboardRegionToken> => ({ t: 'value', value })),
        endPromise.then((r): Waited<WhiteboardRegionToken> => ({ t: 'end', r })),
      ]),
    );
    if (tokenOrEnd === 'timeout') return finishFail(fail('timeout'));
    if (tokenOrEnd.t === 'end') return finishFail(tokenOrEnd.r);
    const token = tokenOrEnd.value;

    // (4) 各 PUT (並列) → confirm。token の items は srcLayerId で対応づける。
    const puts: Array<Promise<{ ok: boolean; message?: string }>> = [];
    const used = new Set<number>();
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const t = token.items.find(
        (x) => x.srcLayerId === item?.srcLayerId && !used.has(x.srcLayerId),
      );
      if (item === undefined || t === undefined) return finishFail(fail('token-mismatch'));
      used.add(t.srcLayerId);
      puts.push(
        runImagepastePut({
          putUrl: t.putUrl,
          file: new Blob([(pngs[i] as Uint8Array).slice()], { type: 'image/png' }),
          contentType: 'image/png',
          ...(args.putFetchFn !== undefined ? { fetchFn: args.putFetchFn } : {}),
        }).then((o) => (o.outcome === 'ok' ? { ok: true } : { ok: false, message: o.message })),
      );
    }
    const results = await Promise.all(puts);
    const bad = results.find((r) => !r.ok);
    if (bad !== undefined) {
      return finishFail(
        fail('put-failed', bad.message !== undefined ? { message: bad.message } : {}),
      );
    }
    transport.send({ t: 'whiteboard.region-confirm', requestId });

    // (5) echo (適用済み) か rejected を待つ。成功なら hold は engine が echo で外す。
    if (echoSeen) {
      settled = true;
      return { ok: true, requestId };
    }
    const end = await timeoutResult(args.echoTimeoutMs ?? REGION_ECHO_TIMEOUT_MS, endPromise);
    if (end === 'timeout') return finishFail(fail('timeout'));
    if (!end.ok) return finishFail(end);
    settled = true;
    return end;
  } finally {
    unsub();
    // 例外で抜けたときも保持を残さない。
    if (!settled) engine.releaseOwnStrokeInput();
  }
};

/** 拒否 reason / 失敗の日本語文言 (トースト用)。 */
export const regionMoveFailText = (
  reason: RegionRejectReason | RegionLocalFailure,
  retryAfterMs?: number,
): string => {
  switch (reason) {
    case 'rate-limited': {
      const sec = Math.max(1, Math.ceil((retryAfterMs ?? 1_000) / 1_000));
      return `範囲の移動は あと ${sec} 秒 待ってね`;
    }
    case 'content-too-large':
      return '範囲の画像が大きすぎます (1枚 5MB・合計 15MB まで)';
    case 'invalid-region':
      return 'その範囲は移動できません';
    case 'buffer-full':
      return '今は移動できません (少し待ってからやり直してね)';
    case 'upload-failed':
    case 'put-failed':
      return '範囲の画像のアップロードに失敗しました';
    case 'invalid-upload':
    case 'token-mismatch':
      return '範囲の移動に失敗しました (やり直してね)';
    case 'read-failed':
      return '範囲を読み取れませんでした';
    case 'timeout':
      return '範囲の移動が時間内に終わりませんでした';
  }
};
